/* eslint-disable max-lines-per-function */
import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "graphql-tag";
import Switch from "@material-ui/core/Switch";
import { FormControlLabel as MuiFormControlLabel,
  MenuItem, Select, withStyles,
  FormControl, FormHelperText,
  InputLabel, Tooltip, Typography,
  Grid } from "@material-ui/core";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import { Viewer } from "components/auth/types";
import { usePropertyFeature } from "components/feature";
import {
  Feature, FormType, getListById,
  getListById_interestList_property_forms as Form,
  toggleFormOpen as toggleFormOpenType,
  setListDefaultSorting as setListDefaultSortingType,
  setListDefaultSortingVariables,
  setPropertyTimezone,
  setPropertyTimezoneVariables,
} from "types/gql-types";
import { humanizeFormType } from "utils/form-types";
import { useSnackbar } from "components/haven-design/snackbar";
import Copyable from "components/copyable";
import { Timezone } from "pages/dashboard/admin/import/interest_imports/types";
import { LIST } from "../queries";
import { RespondToEmailForm } from "./respond-to-email";
import { DailyDigestForm } from "./daily-digest";
import { ReinstatementEmailForm } from "./reinstatement-email";
import WaitTimeSelect from "./wait-time-component";

export const TOGGLE_FORM_OPEN = gql`
  mutation toggleFormOpen($id: ID!) {
    toggleFormOpen(id: $id) {
      id
    }
  }
`;

export const TOGGLE_FORM_DISPLAY = gql`
  mutation toggleFormDisplay($id: ID!) {
    toggleFormDisplay(id: $id) {
      id
    }
  }
`;


const LIST_DEFAULT_SORT_MUTATION = gql`
  mutation setListDefaultSorting($id: ID!, $defaultSorting: String!) {
    setListDefaultSorting(id: $id, defaultSorting: $defaultSorting) {
      id,
      defaultSorting
    }
  }
`;

const PROPERTY_TIMEZONE_MUTATION = gql`
  mutation setPropertyTimezone($propertyId: ID!, $timezone: String!) {
    setPropertyTimezone(id: $propertyId, timezone: $timezone) {
      id,
      timezone
    }
  }
`;

interface Props {
  path: string;
  listId: string;
  propertyId: string;
  viewer: Viewer;
}

const FormControlLabel = withStyles(() => ({
  label: {
    display: "inline-block",
    color: "#c7a90d",
    fontWeight: 600,
  },
}))(MuiFormControlLabel);

const weightsDisabledMessage = "Weighted Preferences must be enabled by Haven Connect. Please contact support@havenconnect.com to get started";
const weightsEnabledMessage = "Weights are enabled. Please contact support@havenconnect.com to remove weighted preferences.";

const WeightedMenuItem: React.FC<{value: string, disabled?: boolean}> =
({ value, disabled = false, children, ...props }) => (
  <Tooltip title={disabled ? weightsDisabledMessage : weightsEnabledMessage}>
    <span>
      <MenuItem value={value} disabled={disabled} {...props}>
        {children}
      </MenuItem>
    </span>
  </Tooltip>);

const formSort = (a:Form, b:Form):number => {
  const before = -1;
  const after = 1;
  switch (a.type) {
    case FormType.PRE_APPLICATION:
      return before;
    case FormType.FULL_APPLICATION:
      if (b.type === FormType.PRE_APPLICATION) return after;
      return before;
    default:
      if (a.type < b.type) return before;
      if (a.type > b.type) return after;
      return 0;
  }
};

export const ListSettingsSubPage: React.FC<Props> = (props) => {
  const { listId, viewer, propertyId } = props;
  const { openSnackbar } = useSnackbar();

  const { data, loading, error, refetch } = useQuery<getListById>(LIST, {
    fetchPolicy: "cache-first",
    variables: { id: listId },
  });

  const weightedPrefsEnabled = usePropertyFeature(props.propertyId, Feature.WEIGHTED_PREFERENCES);
  const [toggleFormOpen, { loading: formOpenLoading }] =
    useMutation<toggleFormOpenType>(TOGGLE_FORM_OPEN);
  // const [toggleFormDisplay, { loading: formDisplayLoading }] =
  //   useMutation<toggleFormDisplay>(TOGGLE_FORM_DISPLAY);
  const requestReinstatementEnabled =
    usePropertyFeature(props.propertyId, Feature.REQUEST_REINSTATEMENT);
  const [setListDefaultSorting, defaultSortingMutation] =
    useMutation<setListDefaultSortingType, setListDefaultSortingVariables>(
      LIST_DEFAULT_SORT_MUTATION,
    );
  const [setTimezone, setTimezoneMutation] =
    useMutation<setPropertyTimezone, setPropertyTimezoneVariables>(
      PROPERTY_TIMEZONE_MUTATION,
    );

  if (error) return <ServerError desc={error.message} />;
  if (
    loading ||
    weightedPrefsEnabled === null
  ) {
    return <LoadingSpinner />;
  }
  if (!data) return <ServerError desc="Missing Data" />;

  const list = data.interestList;
  const forms = [...list.property.forms].sort(formSort);
  const hasSubmissionForm = forms.some((form) => form.type === FormType.INTEREST_LIST_SUBMISSION);

  const changeDefaultSorting = (sorting: string) => {
    setListDefaultSorting({ variables: { id: list.id, defaultSorting: sorting } })
      .then(() => openSnackbar({ message: "Sort order updated.", status: "success" }));
  };

  // Kill whitelabel
  // we're not taking applications at this time
  return (
    <div className="ListSettings" style={{ width: "100%" }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {!loading && (
            <div style={{ marginTop: -20 }}>
              {forms?.map((form) => (
                <div key={form.id} style={{ marginTop: 20 }}>
                  <Typography variant="h6">{list.name} {humanizeFormType(form.type)}</Typography>
                  <div>
                    <FormControlLabel
                      id="form-open-switch-label"
                      control={
                        <Switch
                          id="form-open-switch"
                          checked={form.open} onChange={() => {
                            if (!formOpenLoading) {
                              toggleFormOpen({ variables: { id: form.id } })
                                .finally(refetch);
                            }
                          }} />}
                      label={`${humanizeFormType(form.type)} is currently ${form.open ? "Open" : "Closed"}`}
                    />
                    {/* <FormControlLabel
                      id="form-display-switch-label"
                      control={
                        <Switch
                        id="form-display-switch"
                        checked={form.display} onChange={() => {
                          console.log("formDisplay", form.display);
                          if (!formDisplayLoading) {
                            toggleFormDisplay({ variables: { id: form.id } })
                              .finally(refetch);
                          }
                        }} />}
                      label={`${humanizeFormType(form.type)} is currently ${form.display ? "Shown" : "Hidden"}`}
                    /> */}
                  </div>
                  <Copyable text={`${window.location.origin}/form/${form.id}`} />
                </div>
              ))}
            </div>
          )}
        </Grid>

        <Grid item xs={12} md={7}>
          <Typography variant="h6">Timezone</Typography>
          <Typography variant="subtitle2">
            Select the timezone which the property resides in.
          </Typography>
          <Select
            variant="outlined"
            disabled={setTimezoneMutation.loading}
            value={list.property.timezone}
            onChange={event => setTimezone({ variables: { propertyId, timezone: event.target.value as Timezone } })}
            style={{ minWidth: "8em" }}
          >
            <MenuItem value="none" disabled>Choose</MenuItem>
            <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
            <MenuItem value="America/Denver">Mountain Time</MenuItem>
            <MenuItem value="America/Chicago">Central Time</MenuItem>
            <MenuItem value="America/New_York">Eastern Time</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={12} md={7}>
          <Typography variant="h6">Default list order</Typography>
          <div>
            <FormControl fullWidth error={Boolean(defaultSortingMutation.error)}>
              <InputLabel id="select-order-label">Select</InputLabel>
              <Select
                id="select-order"
                labelId="select-order-label"
                variant="outlined"
                onChange={(e) => changeDefaultSorting(e.target.value as string)}
                disabled={defaultSortingMutation.loading}
                value={list.defaultSorting || ""}
                error={Boolean(defaultSortingMutation.error)}
              >
                <MenuItem disabled={weightedPrefsEnabled} value="POSITION_NUMBER">
                  Position Number</MenuItem>
                <MenuItem disabled={weightedPrefsEnabled} value="SUBMIT_DATE_THEN_POSITION_NUMBER">
                  Submit Date/Time then Position Number</MenuItem>
                <WeightedMenuItem disabled={!weightedPrefsEnabled} value="SUBMIT_DATE_THEN_WEIGHTED_PREFERENCES">
                  Submit Date/Time then Weighted Preferences </WeightedMenuItem>
                <WeightedMenuItem disabled={!weightedPrefsEnabled} value="WEIGHTED_PREFERENCES_THEN_SUBMIT_DATE">
                  Weighted Preferences then Submit Date</WeightedMenuItem>
                <WeightedMenuItem disabled={!weightedPrefsEnabled} value="WEIGHTED_PREFERENCES_THEN_POSITION_NUMBER">
                  Weighted Preferences then Position Number</WeightedMenuItem>
              </Select>
              {defaultSortingMutation.error && (<FormHelperText error>
                Error chainging default sorting, contact support.</FormHelperText>)}
            </FormControl>
          </div>
        </Grid>
        {!hasSubmissionForm && (
          <Grid item xs={12} md={6}>
            <Typography variant="h6" style={{ paddingBottom: "0.5em" }}>Estimated Wait Time</Typography>
            <WaitTimeSelect label="Estimated Wait Time" interestListId={list.id}
              initialValue={list.waitTime} />
          </Grid>
        )}
        {viewer.isSupervisor && (
          <RespondToEmailForm propertyId={propertyId} />
        )}
        <Grid item xs={12}>
          <DailyDigestForm propertyId={propertyId} />
        </Grid>
        {requestReinstatementEnabled && (
          <Grid item xs={12}>
            <ReinstatementEmailForm propertyId={propertyId} />
          </Grid>
        )}
      </Grid>
    </div>
  );
};
